<script setup>
import { computed, ref, onUnmounted } from 'vue';
import { useRoute, onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import SettingsHeader from 'dashboard/components-next/captain/pageComponents/settings/SettingsHeader.vue';
import AssistantBehaviorForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantBehaviorForm.vue';
import AssistantAutoResolveForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantAutoResolveForm.vue';
import AssistantFollowupForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantFollowupForm.vue';
import AssistantHandoffRulesForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantHandoffRulesForm.vue';
import AssistantModelForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantModelForm.vue';
import AssistantMessageTemplatesForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantMessageTemplatesForm.vue';
import PromptVersionsList from 'dashboard/components-next/captain/pageComponents/assistant/settings/PromptVersionsList.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import UnsavedChangesDialog from 'dashboard/components-next/captain/pageComponents/assistant/settings/UnsavedChangesDialog.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();

const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const isUpdating = computed(() => uiFlags.value.updatingItem);
const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);

const behaviorFormRef = ref(null);
const modelFormRef = ref(null);
const messageTemplatesFormRef = ref(null);
const handoffRulesFormRef = ref(null);
const autoResolveFormRef = ref(null);
const followupFormRef = ref(null);
const unsavedChangesDialogRef = ref(null);
const versionsDialogRef = ref(null);

const handleVersionRestored = async () => {
  await store.dispatch('captainAssistants/show', assistantId.value);
  versionsDialogRef.value?.close();
};

// Each section exposes its own isDirty (computed from its own state vs. the
// snapshot taken when assistant last loaded) — the page-level flag is just
// "is any of the 4 sections dirty", so it stays correct without needing any
// section to notify the parent on every keystroke.
const isDirty = computed(() =>
  [
    behaviorFormRef,
    modelFormRef,
    messageTemplatesFormRef,
    handoffRulesFormRef,
    autoResolveFormRef,
    followupFormRef,
  ].some(formRef => formRef.value?.isDirty)
);

const handleBeforeUnload = event => {
  if (!isDirty.value) return;
  // Custom text is ignored by modern browsers, which show their own
  // generic prompt — this is the maximum control the platform allows here.
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};
window.addEventListener('beforeunload', handleBeforeUnload);
onUnmounted(() =>
  window.removeEventListener('beforeunload', handleBeforeUnload)
);

// Each section builds its own { config: {...} } (and, for Behavior, also
// { name }) from its own local state — never from a stale
// assistant.config snapshot — so merging them here is the only place
// config gets combined, and no section can silently drop another
// section's unsaved edits.
const buildAggregatedPayload = () => {
  const sectionPayloads = [
    behaviorFormRef.value.getPayload(),
    modelFormRef.value.getPayload(),
    messageTemplatesFormRef.value.getPayload(),
    handoffRulesFormRef.value.getPayload(),
    autoResolveFormRef.value.getPayload(),
    followupFormRef.value.getPayload(),
  ];

  return sectionPayloads.reduce(
    (merged, section) => ({
      ...merged,
      ...section,
      config: { ...merged.config, ...section.config },
    }),
    { config: {} }
  );
};

// PageLayout's header button has no disabled/isLoading prop of its own, so
// a double-click could fire two concurrent PATCHes — guard here instead.
const saveAssistant = async () => {
  if (isUpdating.value) return false;

  const isValid = await behaviorFormRef.value.validate();
  if (!isValid) return false;

  if (!handoffRulesFormRef.value.validate()) {
    useAlert(
      t(
        'CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DISTRIBUTION.SELECT_USER_REQUIRED'
      )
    );
    return false;
  }

  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      ...buildAggregatedPayload(),
    });
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
    return true;
  } catch (error) {
    const errorMessage =
      error?.message || t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
    return false;
  }
};

let pendingLeave = null;

onBeforeRouteLeave(() => {
  if (!isDirty.value) return true;

  return new Promise(resolve => {
    pendingLeave = resolve;
    unsavedChangesDialogRef.value.dialogRef.open();
  });
});

const handleSaveChanges = async () => {
  const saved = await saveAssistant();
  const resolveLeave = pendingLeave;
  pendingLeave = null;
  if (saved) unsavedChangesDialogRef.value.dialogRef.close();
  resolveLeave?.(saved);
};

const handleContinueEditing = () => {
  pendingLeave?.(false);
  pendingLeave = null;
};

const handleDiscardAndLeave = () => {
  pendingLeave?.(true);
  pendingLeave = null;
};
</script>

<template>
  <PageLayout
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
    :button-label="t('CAPTAIN.ASSISTANTS.FORM.UPDATE')"
    button-icon=""
    :button-disabled="isUpdating"
    :button-loading="isUpdating"
    @click="saveAssistant"
  >
    <template #search>
      <Button
        v-tooltip.top="t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.TITLE')"
        icon="i-lucide-history"
        size="sm"
        variant="faded"
        color="slate"
        @click="versionsDialogRef.open()"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <SettingsHeader
          :heading="t('CAPTAIN.ASSISTANTS.SETTINGS.BEHAVIOR.TITLE')"
          :description="t('CAPTAIN.ASSISTANTS.SETTINGS.BEHAVIOR.DESCRIPTION')"
        />
        <AssistantBehaviorForm ref="behaviorFormRef" :assistant="assistant" />

        <span class="w-full h-px bg-n-weak" />

        <SettingsHeader
          :heading="t('CAPTAIN.ASSISTANTS.FORM.MODEL.TITLE')"
          :description="t('CAPTAIN.ASSISTANTS.FORM.MODEL.DESCRIPTION')"
        />
        <AssistantModelForm ref="modelFormRef" :assistant="assistant" />

        <span class="w-full h-px bg-n-weak" />

        <SettingsHeader
          :heading="t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.TITLE')"
          :description="
            t('CAPTAIN.ASSISTANTS.FORM.MESSAGE_TEMPLATES.DESCRIPTION')
          "
        />
        <AssistantMessageTemplatesForm
          ref="messageTemplatesFormRef"
          :assistant="assistant"
        />

        <span class="w-full h-px bg-n-weak" />

        <SettingsHeader
          :heading="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.TITLE')"
          :description="t('CAPTAIN.ASSISTANTS.FORM.FOLLOWUP.DESCRIPTION')"
        />
        <AssistantFollowupForm ref="followupFormRef" :assistant="assistant" />

        <span class="w-full h-px bg-n-weak" />

        <SettingsHeader
          :heading="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.TITLE')"
          :description="t('CAPTAIN.ASSISTANTS.FORM.HANDOFF_RULES.DESCRIPTION')"
        />
        <AssistantHandoffRulesForm
          ref="handoffRulesFormRef"
          :assistant="assistant"
        />

        <span class="w-full h-px bg-n-weak" />

        <SettingsHeader
          :heading="t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.TITLE')"
          :description="t('CAPTAIN.ASSISTANTS.FORM.AUTO_RESOLVE.DESCRIPTION')"
        />
        <AssistantAutoResolveForm
          ref="autoResolveFormRef"
          :assistant="assistant"
        />
      </div>
    </template>
  </PageLayout>

  <Dialog
    ref="versionsDialogRef"
    :title="t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.TITLE')"
    :description="t('CAPTAIN.ASSISTANTS.PROMPT_VERSIONS.DESCRIPTION')"
    :show-cancel-button="false"
    :show-confirm-button="false"
    width="2xl"
    overflow-y-auto
  >
    <PromptVersionsList
      :assistant-id="assistantId"
      @restored="handleVersionRestored"
    />
  </Dialog>

  <UnsavedChangesDialog
    ref="unsavedChangesDialogRef"
    :is-saving="isUpdating"
    @save-changes="handleSaveChanges"
    @continue-editing="handleContinueEditing"
    @discard-and-leave="handleDiscardAndLeave"
  />
</template>
